import React from 'react';
import styled from 'styled-components';
import { useMsal } from '@azure/msal-react';
import { 
  User, 
  Mail, 
  Shield, 
  Settings, 
  LogOut, 
  Edit3,
  Calendar,
  Building,
  Activity
} from 'lucide-react';
import { DEV_MODE } from '../authConfig';
import { useDevAuth } from './DevAuthProvider';

const ProfilePageContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 24px;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: #111827;
  margin: 0;
`;

const ProfileGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 2fr;
  gap: 24px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const ProfileHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  margin-bottom: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #e5e7eb;
`;

const ProfileAvatar = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: 700;
  font-size: 24px;
  margin-bottom: 16px;
  box-shadow: 0 4px 16px rgba(59, 130, 246, 0.3);
`;

const ProfileName = styled.h2`
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 4px 0;
`;

const ProfileRole = styled.p`
  font-size: 14px;
  color: #6b7280;
  margin: 0;
`;

const InfoSection = styled.div`
  margin-bottom: 24px;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin: 0 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const InfoGrid = styled.div`
  display: grid;
  gap: 16px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const InfoIcon = styled.div`
  width: 40px;
  height: 40px;
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
`;

const InfoContent = styled.div`
  flex: 1;
`;

const InfoLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
`;

const InfoValue = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: #111827;
`;

const ActionSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ActionButton = styled.button<{ variant?: 'primary' | 'secondary' | 'danger' }>`
  padding: 12px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: 8px;
  border: 2px solid;

  ${props => {
    switch (props.variant) {
      case 'primary':
        return `
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border-color: #3b82f6;
          &:hover {
            background: linear-gradient(135deg, #2563eb 0%, #1e40af 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
          }
        `;
      case 'danger':
        return `
          background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
          color: white;
          border-color: #ef4444;
          &:hover {
            background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(239, 68, 68, 0.4);
          }
        `;
      default:
        return `
          background: white;
          color: #374151;
          border-color: #d1d5db;
          &:hover {
            background: #f9fafb;
            border-color: #9ca3af;
          }
        `;
    }
  }}

  &:focus {
    outline: 2px solid #3b82f6;
    outline-offset: 2px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 10px;
  padding: 20px;
  text-align: center;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 4px;
`;

const StatLabel = styled.div`
  font-size: 12px;
  color: #6b7280;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const UserProfile: React.FC = () => {
  const { instance, accounts } = useMsal();
  const devAuth = DEV_MODE ? useDevAuth() : null;

  const handleLogout = (): void => {
    if (DEV_MODE && devAuth) {
      devAuth.logout();
      return;
    }

    instance.logoutPopup({
      postLogoutRedirectUri: window.location.origin,
    });
  };

  const handleEditProfile = (): void => {
    // TODO: Implement profile editing functionality
    console.log('Edit profile clicked');
  };

  const handleChangePassword = (): void => {
    // TODO: Implement password change functionality
    console.log('Change password clicked');
  };

  // Get account info based on mode
  let account: any = null;
  if (DEV_MODE && devAuth) {
    account = devAuth.user;
  } else if (accounts.length > 0) {
    account = accounts[0];
  }

  if (!account) {
    return (
      <ProfilePageContainer>
        <PageTitle>User Profile</PageTitle>
        <ProfileCard>
          <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
            <User size={48} style={{ margin: '0 auto 16px', opacity: 0.5 }} />
            <p>No user information available</p>
          </div>
        </ProfileCard>
      </ProfilePageContainer>
    );
  }

  const displayName = account.name || account.username || 'User';
  const initials = displayName
    .split(' ')
    .map((name: string) => name.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

  const userEmail = account.username || account.email || 'No email available';
  const userRole = DEV_MODE ? 'Development User' : 'Healthcare Provider';
  const organization = 'CHCA Health';

  return (
    <ProfilePageContainer>
      <PageHeader>
        <PageTitle>User Profile</PageTitle>
      </PageHeader>

      <StatsGrid>
        <StatCard>
          <StatValue>0</StatValue>
          <StatLabel>Total Assessments</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>0</StatValue>
          <StatLabel>This Month</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>0</StatValue>
          <StatLabel>Last 30 Days</StatLabel>
        </StatCard>
      </StatsGrid>

      <ProfileGrid>
        <ProfileCard>
          <ProfileHeader>
            <ProfileAvatar>{initials}</ProfileAvatar>
            <ProfileName>{displayName}</ProfileName>
            <ProfileRole>{userRole}</ProfileRole>
          </ProfileHeader>

          <ActionSection>
            <ActionButton variant="primary" onClick={handleEditProfile}>
              <Edit3 size={16} />
              Edit Profile
            </ActionButton>
            <ActionButton onClick={handleChangePassword}>
              <Settings size={16} />
              Change Password
            </ActionButton>
            <ActionButton variant="danger" onClick={handleLogout}>
              <LogOut size={16} />
              {DEV_MODE ? 'Exit Dev Mode' : 'Logout'}
            </ActionButton>
          </ActionSection>
        </ProfileCard>

        <ProfileCard>
          <InfoSection>
            <SectionTitle>
              <User size={16} />
              Personal Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <User size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Full Name</InfoLabel>
                  <InfoValue>{displayName}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <Mail size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Email Address</InfoLabel>
                  <InfoValue>{userEmail}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <Shield size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Role</InfoLabel>
                  <InfoValue>{userRole}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <Building size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Organization</InfoLabel>
                  <InfoValue>{organization}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </InfoSection>

          <InfoSection>
            <SectionTitle>
              <Activity size={16} />
              Account Information
            </SectionTitle>
            <InfoGrid>
              <InfoItem>
                <InfoIcon>
                  <Calendar size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Member Since</InfoLabel>
                  <InfoValue>{new Date().toLocaleDateString()}</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <Shield size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Account Status</InfoLabel>
                  <InfoValue>Active</InfoValue>
                </InfoContent>
              </InfoItem>
              
              <InfoItem>
                <InfoIcon>
                  <Settings size={16} />
                </InfoIcon>
                <InfoContent>
                  <InfoLabel>Authentication</InfoLabel>
                  <InfoValue>{DEV_MODE ? 'Development Mode' : 'Azure AD'}</InfoValue>
                </InfoContent>
              </InfoItem>
            </InfoGrid>
          </InfoSection>
        </ProfileCard>
      </ProfileGrid>
    </ProfilePageContainer>
  );
};

export default UserProfile;
